import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { useIntl } from 'react-intl';
import Common from 'helpers/intl-common.js';

import FloatingUIBase from './FloatingUIBase';
import ScreenReaderContent from 'components/shared/DesignSystem/shared/components/ScreenReaderContent';

import { baseButtonStyles } from 'components/shared/DesignSystem/shared/styles';

// #region styled-components
const closeButtonSize = '24px';

const Container = styled.span`
  display: block;
  position: relative;
  ${(props) =>
    props.showCloseButton
      ? css`
          padding-block-start: calc(
            var(--cc_size_spacing_xs) + ${closeButtonSize}
          );
        `
      : null}
`;

const CloseButton = styled.button`
  ${baseButtonStyles}
  position: absolute;
  inset-inline-end: var(--cc_size_spacing_xs);
  inset-block-start: var(--cc_size_spacing_xs);
  z-index: 1;
  font-size: var(--cc_size_text_l);
  color: var(--cc_color_link_subtle);
  width: ${closeButtonSize};
  height: ${closeButtonSize};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  border-radius: 100%;

  &:hover {
    color: var(--cc_color_link_secondary);
  }
`;
// #endregion

/**
 * @component
 * Generic CompanyCam popover component - built on top of FloatingUIBase.jsx
 * Does not maintain its own open/close state. Needs to be controlled by consuming app.
 *
 * @param [isOpen] - when true, popover renders.
 * @param [onOpenChange] - callback triggered when popover `isOpen` prop updates. Fired when clicking optional close button.
 * @param [placement] - sets ideal alignment of popover to trigger button. top, top-start, top-end, etc.
 * @param [showCloseButton] - close button visibility. Boolean. Defaults false.
 * @param [showArrow] - arrow visibility. Boolean. Defaults false.
 * @param [ccBackground] - what background color design token to use. Number: 1 through 3
 * @param [ccDepth] - elevation of drop shadow from design tokens. Number: 1 through 3
 * @param [ccBorderRadius] - what border radius design token to use. s, m, l, xl
 * @param [mountNodeId] - specify id of element to mount popover to. Otherwise uses portal appended to end of DOM.
 * @param [id] - assign id to main popover container for testing
 * @param [className] - assign className to main popover container
 * @param [style] - style hook for main popover container
 * @param [trigger] - pass in button element to act as trigger for open/close and positioning the popover. Required.
 * @param [children] - content for the popover. Required.
 * @Param [flipOptions] - https://floating-ui.com/docs/flip has different way to control flip initial and fallback behavior
 *
 * @example
 * import React, { useState } from "react";
 * 
 * const [isOpen, setIsOpen] = useState(false);
 * const handleToggle = () => {
    setIsOpen((prevIsOpen) => !prevIsOpen);
  };
 * 
 * 
 *  <CCPopover
        trigger={
          <button type="button" style={{ display: 'inline-block' }}>
            Trigger Popover
          </button>
        }
        showCloseButton
        showArrow
        placement="bottom-start"
        id="my-id"
        className="my-class-name"
        isOpen={isOpen}
        onOpenChange={handleToggle}
        mountNodeId="main"
      >
        <div style={{ padding: '1.5rem' }}>
            Lorem ipsum dolor sit amet consectetur adipisicing
            elit. Voluptate corporis nemo amet id labore debitis itaque,
            nesciunt animi illo dolore sunt vel, repudiandae in blanditiis
            voluptatem temporibus eos tempora ipsam.lorem
        </div>
      </CCPopover>
 */

/**
 * @deprecated Use { Popover } from `@companycam/slab-web` instead
 *
 * https://slab-web.cocam.co/?path=/docs/popover--docs
 */

const CCPopover = ({
  children,
  trigger,
  placement,
  isOpen,
  onOpenChange,
  style,
  className,
  ccBackground,
  ccDepth,
  ccBorderRadius,
  mountNodeId,
  id,
  showCloseButton,
  showArrow,
  flipOptions,
}) => {
  const intl = useIntl();
  return (
    <FloatingUIBase
      trigger={trigger}
      placement={placement}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      style={style}
      className={className}
      ccBackground={ccBackground}
      ccDepth={ccDepth}
      ccBorderRadius={ccBorderRadius}
      mountNodeId={mountNodeId}
      id={id}
      role="dialog"
      shouldTrapFocus
      showArrow={showArrow}
      interactions={{
        click: true,
        focus: false,
        hover: false,
      }}
      // if you're looking for all possible flipOptions, make some tea and give https://floating-ui.com/docs/flip a gander
      flipOptions={flipOptions}
    >
      <Container showCloseButton={showCloseButton}>
        {showCloseButton ? (
          <CloseButton type="button" onClick={onOpenChange}>
            <i className="mdi mdi-close" aria-hidden="true" />
            <ScreenReaderContent>
              {intl.formatMessage(Common.close)}
            </ScreenReaderContent>
          </CloseButton>
        ) : null}
        {children}
      </Container>
    </FloatingUIBase>
  );
};

CCPopover.propTypes = {
  children: PropTypes.node.isRequired,
  trigger: PropTypes.node.isRequired,
  placement: PropTypes.oneOf([
    'top',
    'top-start',
    'top-end',
    'right',
    'right-start',
    'right-end',
    'bottom',
    'bottom-start',
    'bottom-end',
    'left',
    'left-start',
    'left-end',
  ]),
  isOpen: PropTypes.bool,
  onOpenChange: PropTypes.func,
  style: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  className: PropTypes.string,
  ccBackground: PropTypes.oneOf([1, 2, 3]),
  ccDepth: PropTypes.oneOf([1, 2, 3]),
  ccBorderRadius: PropTypes.oneOf(['s', 'm', 'l', 'xl', 'xxl']),
  id: PropTypes.string,
  mountNodeId: PropTypes.string,
  showCloseButton: PropTypes.bool,
  showArrow: PropTypes.bool,
  flipOptions: PropTypes.object,
};
CCPopover.defaultProps = {
  placement: 'bottom',
  isOpen: true,
  onOpenChange: () => {},
  style: undefined,
  className: undefined,
  ccBackground: 1,
  ccDepth: 3,
  ccBorderRadius: 'l',
  id: undefined,
  mountNodeId: undefined,
  showCloseButton: false,
  showArrow: false,
  flipOptions: null,
};
export default CCPopover;
